
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { useBookingAuth } from "@/hooks/useBookingAuth";

export const useUserDelete = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { accessToken } = useBookingAuth();
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      if (!accessToken) {
        throw new Error("No access token available. Please log in again.");
      }

      setIsDeleting(true);
      console.log("Calling delete-user edge function for user:", userId);
      
      const { data, error } = await supabase.functions.invoke('delete-user', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: { userId }
      });
      
      if (error) {
        console.error("Error calling delete-user function:", error);
        throw new Error(`Failed to delete user: ${error.message}`);
      }
      
      if (!data.success) {
        console.error("Delete user function returned error:", data.error);
        throw new Error(data.error || "Failed to delete user");
      }
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast({ 
        title: "Success", 
        description: "User has been completely deleted from the system" 
      });
      setIsDeleting(false);
    },
    onError: (error: Error) => {
      setIsDeleting(false);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to delete user: ${error.message}`,
      });
    },
  });

  return {
    deleteUserMutation,
    isDeleting
  };
};
